interface Props {
  label: string
  value: string | number
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info'
  hint?: string
  onClick?: () => void
  active?: boolean
  /** Texto de "como analisar" exibido num tooltip ao passar o mouse no ícone ⓘ. */
  analysis?: string
  /** Indicador de meta. status null/ausente → sem selo (card informativo ou sem dado). */
  meta?: { status: 'verde' | 'amarelo' | 'vermelho' | null; label?: string }
}

const TONES = {
  default: 'bg-surface border-line text-ink',
  success: 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  warning: 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  danger:  'bg-red-50 border-red-300 text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300',
  info:    'bg-sky-50 border-sky-300 text-sky-800 dark:bg-sky-500/10 dark:border-sky-500/30 dark:text-sky-300',
}

// Selo de meta — mesma régua dual-theme do ATINGIU em MetasComprasSection.
const META_BADGE = {
  verde:    { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', txt: '✓ Na meta' },
  amarelo:  { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',          txt: 'Atenção' },
  vermelho: { cls: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300',                  txt: '✗ Fora da meta' },
}

export default function KpiCard({ label, value, tone = 'default', hint, onClick, active, analysis, meta }: Props) {
  const interactive = !!onClick
  return (
    <div
      onClick={onClick}
      className={`relative border rounded-xl md:rounded-lg px-3 py-2 transition-all ${TONES[tone]} ${
        interactive ? 'cursor-pointer hover:brightness-95' : ''
      } ${active ? 'ring-2 ring-ink/50' : ''}`}
    >
      {analysis && (
        <span
          className="group/info absolute top-1.5 right-1.5 z-10"
          onClick={e => e.stopPropagation()}
        >
          <span
            className="flex items-center justify-center w-3.5 h-3.5 rounded-full border border-current/40 text-[9px] font-bold opacity-50 hover:opacity-90 cursor-help"
            aria-label="Como analisar"
          >
            i
          </span>
          <span
            role="tooltip"
            className="pointer-events-none absolute right-0 top-4 z-20 hidden group-hover/info:block w-56 rounded-lg border border-line bg-surface p-2.5 text-[11px] font-normal leading-snug text-ink-muted normal-case tracking-normal shadow-xl"
          >
            {analysis}
          </span>
        </span>
      )}
      <p className="text-[10px] uppercase tracking-wide opacity-70 pr-4 truncate">{label}</p>
      <p className="text-xl font-bold tabular-nums leading-tight">{value}</p>
      {hint && <p className="text-[10px] opacity-60 leading-tight truncate">{hint}</p>}
      {meta?.status && (
        <span className={`mt-1 inline-flex items-center gap-1 self-start px-1.5 py-0.5 rounded text-[10px] font-bold ${META_BADGE[meta.status].cls}`}>
          {META_BADGE[meta.status].txt}
          {meta.label && <span className="font-normal opacity-70">· {meta.label}</span>}
        </span>
      )}
    </div>
  )
}
